import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import {
  fetchPublicTicketByToken,
  isEventTicketApiConfigured,
} from 'services/eventTicketService';

const TNO_LOGO_URL = 'https://ads.tnonetwork.com/images/logos/logo_white.PNG';

const styles = {
  root: {
    minHeight: '100vh',
    background: 'radial-gradient(ellipse at 50% 0%, #1a2744 0%, #06060a 55%, #030308 100%)',
    color: '#e8eaef',
    padding: '24px 16px 48px',
    boxSizing: 'border-box',
    fontFamily:
      'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  glow: {
    textAlign: 'center',
    marginBottom: 28,
  },
  logo: {
    width: 'min(220px, 70vw)',
    maxWidth: '100%',
    height: 'auto',
    display: 'block',
    margin: '0 auto',
  },
  sub: {
    marginTop: 10,
    fontSize: 11,
    letterSpacing: '0.2em',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.45)',
  },
  card: {
    maxWidth: 400,
    margin: '0 auto',
    borderRadius: 20,
    padding: '28px 22px 32px',
    background: 'linear-gradient(145deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02))',
    border: '1px solid rgba(255,255,255,0.12)',
    boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
    backdropFilter: 'blur(12px)',
  },
  label: {
    fontSize: 11,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.5)',
    marginBottom: 6,
  },
  name: {
    fontSize: 'clamp(1.35rem, 4.5vw, 1.65rem)',
    fontWeight: 700,
    marginBottom: 20,
    lineHeight: 1.25,
  },
  badge: {
    display: 'inline-block',
    padding: '8px 14px',
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 600,
    background: 'rgba(124, 92, 255, 0.25)',
    border: '1px solid rgba(167, 139, 250, 0.45)',
    color: '#e8deff',
    marginBottom: 24,
  },
  qrWrap: {
    display: 'flex',
    justifyContent: 'center',
    padding: 16,
    background: '#fff',
    borderRadius: 16,
    marginBottom: 16,
  },
  foot: {
    textAlign: 'center',
    fontSize: 12,
    color: 'rgba(255,255,255,0.4)',
    lineHeight: 1.5,
  },
  statusOk: {
    textAlign: 'center',
    fontSize: 14,
    fontWeight: 600,
    color: '#5ee9a0',
    marginBottom: 12,
  },
  statusWarn: {
    textAlign: 'center',
    fontSize: 14,
    fontWeight: 600,
    color: '#ffb86b',
    marginBottom: 12,
  },
  errBox: {
    maxWidth: 400,
    margin: '40px auto 0',
    padding: 20,
    borderRadius: 16,
    background: 'rgba(180,40,40,0.15)',
    border: '1px solid rgba(255,100,100,0.35)',
    color: '#ffc9c9',
    textAlign: 'center',
    fontSize: 14,
  },
  cfgBox: {
    maxWidth: 400,
    margin: '40px auto 0',
    padding: 20,
    borderRadius: 16,
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.12)',
    color: 'rgba(255,255,255,0.75)',
    fontSize: 14,
    lineHeight: 1.55,
  },
};

function TicketCredentialPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ticket, setTicket] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!isEventTicketApiConfigured()) {
        setError('config');
        setLoading(false);
        return;
      }
      const r = await fetchPublicTicketByToken(token);
      if (cancelled) return;
      if (!r.ok) {
        setError(r.error || 'load_failed');
        setLoading(false);
        return;
      }
      setTicket(r.ticket);
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <>
      <style>{`
        @keyframes tnoShimmer {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
      `}</style>
      <div style={styles.root}>
        <div style={styles.glow}>
          <img src={TNO_LOGO_URL} alt="TNO Network" style={styles.logo} />
          <div style={styles.sub}>Credencial digital</div>
        </div>

        {loading && (
          <p style={{ textAlign: 'center', opacity: 0.7 }}>Cargando…</p>
        )}

        {error === 'config' && (
          <div style={styles.cfgBox}>
            No hay URL de API (solo ocurre fuera del navegador). En el sitio público se usa{' '}
            <code style={{ color: '#9ec5ff' }}>/ticket-api</code> en el mismo dominio; en local define{' '}
            <code style={{ color: '#9ec5ff' }}>REACT_APP_EVENT_TICKET_SERVICE_URL</code>.
          </div>
        )}

        {error && error !== 'config' && (
          <div style={styles.errBox}>
            No encontramos esta credencial o el enlace no es válido.
          </div>
        )}

        {ticket && (
          <div style={styles.card}>
            {ticket.status === 'checked_in' ? (
              <div style={styles.statusWarn}>
                Ya registrado en entrada
                {ticket.checkin_time
                  ? ` · ${new Date(ticket.checkin_time).toLocaleString('es-VE', { dateStyle: 'short', timeStyle: 'short' })}`
                  : ''}
              </div>
            ) : (
              <div style={styles.statusOk}>Listo para mostrar en la entrada</div>
            )}
            <div style={styles.label}>Nombre</div>
            <div style={styles.name}>{ticket.attendee_name}</div>
            <div style={styles.label}>Tipo de acceso</div>
            <div style={styles.badge}>{ticket.access_type}</div>
            <div style={styles.label}>Código en puerta</div>
            <div style={styles.qrWrap}>
              <QRCodeSVG
                value={ticket.access_token}
                size={220}
                level="M"
                includeMargin={false}
              />
            </div>
            <div style={styles.foot}>
              Evento: {ticket.event_slug}
              <br />
              Presenta este QR en vivo (no solo captura).
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default TicketCredentialPage;
